/**
 * FELVORA — Vertical "Intermediación y comercialización de bienes inmuebles"
 * (Art. 17 Fr. V LFPIORPI): dashboard de inicio en fase PLD PURA.
 *
 * feb-2026 · iter 07-26: los módulos administrativos (inmuebles, pipeline, asesores,
 * arrendamientos, egresos, comisiones, reportes) están APAGADOS. Esta pantalla ahora
 * refleja el estado de CUMPLIMIENTO — no la operación comercial — con KPIs de:
 *   · Prospectos con screening
 *   · Clientes activos con expediente
 *   · Operaciones abiertas
 *   · Avisos SPPLD del período
 * Los datos vienen de endpoints ya existentes (prospectos, clientes, operaciones,
 * avisos). Los links llevan a los 7 módulos vivos del vertical.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Users, FileText, Send, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { giroNombreLargo, giroArticulo } from "@/lib/actividad_vulnerable";
import { useMiActividad } from "@/hooks/useMiActividad";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export default function InicioInmo() {
  const [prospectos, setProspectos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [operaciones, setOperaciones] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const act = useMiActividad();
  // El nombre oficial y la fracción salen del catálogo del backend (una actividad puede
  // operar varias fracciones, como V / V Bis / XV). `user.giro` solo sirve de respaldo:
  // en el primer render tras el login todavía no viene en el payload de la sesión.
  const giroLargo = act.nombreLargo || giroNombreLargo(user?.giro);
  const articulo = act.fracciones.length ? act.articulo : giroArticulo(user?.giro);

  useEffect(() => {
    Promise.all([
      api.get("/prospectos").then((r) => setProspectos(r.data)).catch(() => {}),
      api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {}),
      api.get("/inmobiliaria/operaciones").then((r) => setOperaciones(r.data)).catch(() => {}),
      api.get("/inmobiliaria/avisos").then((r) => setAvisos(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const clientesActivos = clientes.filter((c) => c.estatus_cliente === "Activo").length;
  const expedientesCompletos = clientes.filter((c) => c.expediente_completo).length;
  const opsAbiertas = operaciones.filter((o) => o.estatus && o.estatus !== "cerrada" && o.estatus !== "cancelada").length;
  const opsAviso = operaciones.filter((o) => o.objeto_de_aviso).length;
  const avisosEnviados = avisos.length;

  return (
    <div className="space-y-6" data-testid="inmobiliaria-inicio">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-slate-900"
            data-testid="inmobiliaria-inicio-title">
          Bienvenido a tu operación de cumplimiento PLD
        </h1>
        <p className="text-sm text-slate-500">
          {giroLargo} · LFPIORPI {articulo}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card data-testid="kpi-prospectos">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-slate-600">
              <UserPlus className="h-4 w-4" /> Prospectos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold text-slate-900">{loading ? "—" : prospectos.length}</p>
            <p className="text-xs text-slate-500">con screening automático</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-clientes">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-slate-600">
              <Users className="h-4 w-4" /> Clientes activos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold text-slate-900">{loading ? "—" : clientesActivos}</p>
            <p className="text-xs text-slate-500">{expedientesCompletos} con expediente completo</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-operaciones">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-slate-600">
              <FileText className="h-4 w-4" /> Operaciones abiertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold text-slate-900">{loading ? "—" : opsAbiertas}</p>
            <p className="text-xs text-slate-500">{opsAviso} sujetas a aviso</p>
          </CardContent>
        </Card>
        <Card data-testid="kpi-avisos">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm text-slate-600">
              <Send className="h-4 w-4" /> Avisos SPPLD
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold text-slate-900">{loading ? "—" : avisosEnviados}</p>
            <p className="text-xs text-slate-500">enviados a la fecha</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-forest-600" /> Accesos rápidos
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Link to="/r/prospectos_av" className="flex items-center justify-between rounded-md border border-slate-100 bg-white p-3 text-emerald-700 hover:bg-slate-50 hover:underline"
                data-testid="link-prospectos">
            Prospectos con screening <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/r/clientes_av" className="flex items-center justify-between rounded-md border border-slate-100 bg-white p-3 text-emerald-700 hover:bg-slate-50 hover:underline"
                data-testid="link-clientes">
            Clientes y expedientes <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/inmobiliaria/operaciones" className="flex items-center justify-between rounded-md border border-slate-100 bg-white p-3 text-emerald-700 hover:bg-slate-50 hover:underline"
                data-testid="link-operaciones">
            Operaciones {articulo} <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/inmobiliaria/avisos" className="flex items-center justify-between rounded-md border border-slate-100 bg-white p-3 text-emerald-700 hover:bg-slate-50 hover:underline"
                data-testid="link-avisos">
            Avisos SPPLD <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/inmobiliaria/cumplimiento" className="flex items-center justify-between rounded-md border border-slate-100 bg-white p-3 text-emerald-700 hover:bg-slate-50 hover:underline"
                data-testid="link-cumplimiento">
            EBR y bitácora de cumplimiento <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/inmobiliaria/configuracion" className="flex items-center justify-between rounded-md border border-slate-100 bg-white p-3 text-emerald-700 hover:bg-slate-50 hover:underline"
                data-testid="link-configuracion">
            Configuración de la entidad <ArrowRight className="h-4 w-4" />
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
